import { useRef, useState } from 'react'
import {
  Banner,
  Button,
  Card,
  IconButton,
  Input,
  Lightbox,
  SectionLabel,
  type LightboxImage,
} from '../../design/components'
import { useGallery, useGalleryEditing } from './queries'
import {
  hasImageKinds,
  type GalleryImage,
  type GalleryOwner,
  type LocationImageType,
} from './types'
import styles from './ImageGallery.module.css'

/**
 * One gallery, for a character or a location.
 *
 * They are the same resource behind two routes and one component serves both: the only
 * differences are the path (in `galleryPath`) and whether image *kinds* apply, which is a
 * property of the owner rather than of any image.
 *
 * **`canManage` is the server's word.** Every upload, retitle, set-main and delete control
 * hangs off the flag the gallery ships, so no screen re-implements "owner, or the Game
 * Master of the campaign this location is in" to decide whether to draw a button.
 *
 * Image URLs point at `/fileserver/files/{path}` and are **unauthenticated** — anyone
 * holding a path can fetch one. That is recorded in the plan's §3 and the architecture
 * doc as an API-side gap; this client consumes them as they come and cannot fix it.
 */
export function ImageGallery({ owner, label = 'Images' }: { owner: GalleryOwner; label?: string }) {
  const query = useGallery(owner)
  const editing = useGalleryEditing(owner)
  const [uploading, setUploading] = useState(false)

  // Which image is open full screen, by position — null when none is. Position rather than
  // id because the lightbox pages through the set, and "which one am I on" is exactly an
  // index into the list the grid is already showing.
  const [openAt, setOpenAt] = useState<number | null>(null)

  const gallery = query.data
  const images = gallery?.images ?? []
  const canManage = gallery?.canManage === true

  // The originals, not the thumbnails the grid draws: full screen is the one place the
  // full-size file is worth its bytes.
  const full: LightboxImage[] = images.map((image) => ({
    src: image.url ?? image.urlSmall ?? image.urlThumb ?? '',
    alt: image.title ?? 'Gallery image',
    caption: image.title ?? undefined,
  }))

  return (
    <Card
      variant="bio"
      label={label}
      hint={images.length === 1 ? '1 image' : `${images.length} images`}
      action={
        canManage && !uploading ? (
          <IconButton label="Upload an image" icon="add" onClick={() => setUploading(true)} />
        ) : undefined
      }
    >
      {query.isPending && <p className={styles.note}>Loading…</p>}

      {query.isError && <Banner tone="error">Could not load this gallery.</Banner>}

      {editing.failure !== null && (
        <Banner
          tone="error"
          actions={
            <Button variant="secondary" onClick={editing.dismissFailure}>
              Dismiss
            </Button>
          }
        >
          {editing.failure}
        </Banner>
      )}

      {query.isSuccess && images.length === 0 && !uploading && (
        <p className={styles.empty}>
          {canManage ? 'Nothing here yet.' : 'No images have been added.'}
        </p>
      )}

      {images.length > 0 && (
        <div className={styles.grid}>
          {images.map((image, index) => (
            <ImageTile
              key={image.id}
              image={image}
              canManage={canManage}
              editing={editing}
              onOpen={() => setOpenAt(index)}
            />
          ))}
        </div>
      )}

      {openAt !== null && full.length > 0 && (
        <Lightbox images={full} index={openAt} onClose={() => setOpenAt(null)} />
      )}

      {canManage && uploading && (
        <UploadForm owner={owner} editing={editing} onClose={() => setUploading(false)} />
      )}
    </Card>
  )
}

function ImageTile({
  image,
  canManage,
  editing,
  onOpen,
}: {
  image: GalleryImage
  canManage: boolean
  editing: ReturnType<typeof useGalleryEditing>
  onOpen: () => void
}) {
  const [renaming, setRenaming] = useState(false)
  const [title, setTitle] = useState(image.title ?? '')

  const isMain = image.isMain === true

  return (
    <div className={`${styles.tile} ${isMain ? styles.tileMain : ''}`}>
      {/* The thumbnail, not the full image: a gallery of originals is megabytes on a phone.
          The server resolves all three sizes, so picking the small one is a read, not a
          transformation.

          It is a button, because a 140px tile is enough to recognise a picture and not
          enough to look at one — and because a grid whose pictures cannot be opened is the
          one thing every reader tries first. The full-size file is fetched only when one
          is opened, so the grid stays cheap. */}
      <button
        type="button"
        className={styles.thumbButton}
        onClick={onOpen}
        aria-label={`View ${image.title ?? 'this image'} full screen`}
      >
        <img
          className={styles.thumb}
          src={image.urlThumb ?? image.urlSmall ?? image.url ?? ''}
          alt={image.title ?? ''}
          loading="lazy"
        />
      </button>

      {/* Name and state on one line under the picture, then one row of icons. The tile used
          to be three word-buttons that wrapped to three rows on a phone and made the grid
          mostly chrome; a gallery should be pictures. */}
      <div className={styles.tileHead}>
        <span className={styles.rowName}>{image.title ?? 'Untitled'}</span>
        {isMain && <span className={styles.mainBadge}>Main</span>}
        {image.type != null && <span className={styles.tag}>{String(image.type)}</span>}
      </div>

      {renaming ? (
        <form
          className={styles.inlineForm}
          onSubmit={(event) => {
            event.preventDefault()
            editing.update.mutate(
              { imageId: image.id ?? 0, body: { title, description: image.description ?? null } },
              { onSuccess: () => setRenaming(false) },
            )
          }}
        >
          <Input label="Title" value={title} autoFocus onChange={(e) => setTitle(e.target.value)} />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={editing.update.isPending}>
              {editing.update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      ) : (
        canManage && (
          <div className={styles.tileActions}>
            {/* Icons, each carrying the words as its accessible name and its tooltip. On a
                140px tile three labelled buttons cost more room than the picture; three
                glyphs cost one row, and a screen reader hears the same sentence either way. */}
            <IconButton
              label={`Rename ${image.title ?? 'this image'}`}
              icon="edit"
              onClick={() => setRenaming(true)}
            />
            <IconButton
              label={
                isMain
                  ? 'Already the main image'
                  : `Make ${image.title ?? 'this image'} the main image`
              }
              icon="main"
              className={isMain ? styles.iconOn : undefined}
              disabled={isMain || editing.setMain.isPending}
              onClick={() => editing.setMain.mutate({ imageId: image.id ?? 0 })}
            />
            {/* The main image cannot be deleted — the API refuses it, "Cannot remove the main
                portrait. Please set another image as main first." The control stays, disabled,
                with that reason as its name: a button that vanishes teaches nothing, and the
                sentence used to take a whole line of every main tile. */}
            <IconButton
              label={
                isMain
                  ? 'Set another image as main before deleting this one'
                  : `Delete ${image.title ?? 'this image'}`
              }
              icon="delete"
              className={styles.iconDanger}
              disabled={isMain || editing.remove.isPending}
              onClick={() => editing.remove.mutate({ imageId: image.id ?? 0 })}
            />
          </div>
        )
      )}
    </div>
  )
}

function UploadForm({
  owner,
  editing,
  onClose,
}: {
  owner: GalleryOwner
  editing: ReturnType<typeof useGalleryEditing>
  onClose: () => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [type, setType] = useState<LocationImageType>('Depiction')
  const [error, setError] = useState<string | null>(null)

  const kinds = hasImageKinds(owner)

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        setError(null)

        const file = input.current?.files?.[0]
        if (file === undefined) {
          setError('Choose a file first.')
          return
        }

        editing.upload.mutate(
          {
            file,
            title: title === '' ? undefined : title,
            ...(kinds ? { type } : {}),
          },
          {
            onSuccess: onClose,
            onError: () =>
              // The API enforces JPEG/PNG and a 5 MB ceiling; repeating the rule here is
              // the useful thing to say when it refuses.
              setError('That file was refused. Images must be JPEG or PNG and under 5 MB.'),
          },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        New image
      </SectionLabel>

      {error !== null && <Banner tone="error">{error}</Banner>}

      <label className={styles.fileField}>
        <SectionLabel tone="muted" micro>
          File
        </SectionLabel>
        <input ref={input} className={styles.file} type="file" accept="image/jpeg,image/png" />
      </label>

      <Input
        label="Title"
        value={title}
        hint="Optional — defaults to the file's name."
        onChange={(event) => setTitle(event.target.value)}
      />

      {kinds && (
        <label className={styles.field}>
          <SectionLabel tone="muted" micro>
            Kind
          </SectionLabel>
          <select
            className={styles.select}
            value={type}
            onChange={(event) => setType(event.target.value as LocationImageType)}
          >
            <option value="Depiction">Depiction</option>
            <option value="BattleMap">Battle map</option>
          </select>
        </label>
      )}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={editing.upload.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={editing.upload.isPending}>
          {editing.upload.isPending ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
    </form>
  )
}
